import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { ClipboardCheck, PlayCircle, History, Info, AlertCircle, FileDown, CheckCircle2, ChevronRight, Package, Search, AlertTriangle, Save, X, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { commitInventur, StockApiError, stockErrorToGerman } from '@/lib/stockApi';
import { useAuth } from '@/api/AuthContext';
import { isAfter, subDays, endOfYear } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';

export default function WarehouseInventur() {
  const [activeTab, setActiveTab] = useState('selection'); // selection, counting, results
  const [inventoryType, setInventoryType] = useState(null); // 'opening', 'trial', 'final'
  const [counts, setCounts] = useState({}); // { productId: count }
  const [searchTerm, setSearchTerm] = useState('');
  const [isConfirmingFinish, setIsConfirmingFinish] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  // Sayım başlangıcındaki stok görüntüsü: { [productId]: { stock, variants: {value: stok} } }
  const [countSnapshot, setCountSnapshot] = useState({});

  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Check if Final Inventory (Abschlussinventur) is available (14 days before end of year)
  const isFinalInventoryAvailable = () => {
    const today = new Date();
    const yearEnd = endOfYear(today);
    const fourteenDaysBefore = subDays(yearEnd, 14);
    return isAfter(today, fourteenDaysBefore);
  };

  // Inventur commit akışı: her ürün için commit_inventur Postgres RPC'si
  // çağrılır — master stock + variants JSONB tek atomic adımda yazılır,
  // delta stock_movements tablosuna type='inventur' olarak düşer.
  // Eröffnungsinventur'da is_counted=true ve metadata da set edilir.
  const updateStockMutation = useMutation({
    mutationFn: async (stockUpdates) => {
      const reasonText = inventoryType === 'opening'
        ? 'Eröffnungsinventur'
        : inventoryType === 'final'
        ? 'Abschlussinventur'
        : 'Inventur';
      const markCounted = inventoryType === 'opening';

      const results = [];
      for (const update of stockUpdates) {
        try {
          const res = await commitInventur({
            productId: update.id,
            newStock: Number(update.stock) || 0,
            newVariants: update.variants ?? null,
            actorEmail: user?.email,
            reason: reasonText,
            markCounted,
            expectedStock: update.expectedStock ?? null,
            expectedVariants: update.expectedVariants ?? null,
          });
          results.push(res);
        } catch (err) {
          if (err instanceof StockApiError) {
            throw new Error(stockErrorToGerman(err));
          }
          throw err;
        }
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      toast.success('Bestände erfolgreich aktualisiert');
      setHasSaved(true);
      setActiveTab('results');
      setIsConfirmingFinish(false);
    },
    onError: (err) => {
      toast.error('Fehler: ' + err.message);
      setIsConfirmingFinish(false);
    }
  });

  const handleStartInventory = (type) => {
    setInventoryType(type);
    setActiveTab('counting');
    setHasSaved(false);
    const initialCounts = {};
    const snapshot = {};
    products.forEach(p => {
      let variants = p.variants || [];
      if (typeof variants === 'string') { try { variants = JSON.parse(variants); } catch { variants = []; } }

      // Sayım başlangıcı anlık görüntüsü — onaylarken commit_inventur'a
      // "expected" olarak gönderilir; sayım sırasında işlenen satış/mal
      // kabul hareketleri delta mantığıyla korunur (lost-update önlemi).
      snapshot[p.id] = {
        stock: p.stock || 0,
        variants: Object.fromEntries(variants.map(v => [v.value, v.stock || 0])),
      };

      if (variants.length > 0) {
        variants.forEach((v, idx) => {
          initialCounts[`${p.id}_var_${idx}`] = '';
        });
      } else {
        initialCounts[p.id] = '';
      }
    });
    setCounts(initialCounts);
    setCountSnapshot(snapshot);
  };

  const handleFinishCounting = () => {
    if (inventoryType === 'trial') {
      setActiveTab('results');
    } else {
      setIsConfirmingFinish(true);
    }
  };

  const handleApplyResults = () => {
    const stockUpdates = [];
    
    products.forEach(p => {
      let variants = p.variants || [];
      if (typeof variants === 'string') { try { variants = JSON.parse(variants); } catch { variants = []; } }
      const hasVariants = variants.length > 0;

      if (hasVariants) {
        let anyCounted = false;
        const snapVars = countSnapshot[p.id]?.variants;
        const updatedVariants = variants.map((v, idx) => {
          const val = counts[`${p.id}_var_${idx}`];
          if (val !== '' && val !== undefined && val !== null) {
            anyCounted = true;
            return { ...v, stock: parseInt(val) || 0 };
          }
          // Sayılmayan varyant: snapshot değeriyle gönderilir → delta 0,
          // DB'deki güncel değer (eşzamanlı hareketler dahil) korunur.
          return { ...v, stock: snapVars ? (snapVars[v.value] ?? (v.stock || 0)) : (v.stock || 0) };
        });
        
        if (anyCounted) {
          const totalStock = updatedVariants.reduce((sum, v) => sum + (v.stock || 0), 0);
          const snap = countSnapshot[p.id];
          stockUpdates.push({
            id: p.id,
            stock: totalStock,
            variants: updatedVariants,
            // Sayım başlangıcındaki toplam — sayılmayan varyantlar mevcut
            // değerleriyle toplandığı için expected da aynı mantıkla kurulur.
            expectedStock: snap
              ? variants.reduce((sum, v) => sum + (snap.variants[v.value] ?? (v.stock || 0)), 0)
              : null,
            expectedVariants: snap ? snap.variants : null,
          });
        }
      } else {
        const val = counts[p.id];
        if (val !== '' && val !== undefined && val !== null) {
          stockUpdates.push({
            id: p.id,
            stock: parseInt(val) || 0,
            expectedStock: countSnapshot[p.id]?.stock ?? null,
          });
        }
      }
    });

    if (stockUpdates.length === 0) {
      toast.info('Keine Zählungen zum Speichern vorhanden');
      setIsConfirmingFinish(false);
      return;
    }

    updateStockMutation.mutate(stockUpdates);
  };

  const handleExportPDF = () => {
    toast.info('PDF-Bericht wird generiert...');
    // Real implementation would use html2pdf.js
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // --- RENDERING ---

  if (activeTab === 'selection') {
    return (
      <PageShell
        head={(
            <PageHeader icon={<ClipboardCheck className="w-5 h-5" />} title="Inventurverwaltung" className="mb-2" />
        )}
      >

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Eröffnungsinventur */}
          <Card className="hover:border-purple-200 transition-colors">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-emerald-500" />
                Eröffnungsinventur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                Überträgt die Ist-Bestände nach Abschluss direkt in das Lager (Depo).
              </p>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-10"
                onClick={() => handleStartInventory('opening')}
              >
                Starten
              </Button>
            </CardContent>
          </Card>

          {/* Probeinventur */}
          <Card className="hover:border-purple-200 transition-colors">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                Probeinventur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                Dient zum Vergleich zwischen Soll- ve Ist-Bestand. Keine automatische Bestandsänderung.
              </p>
              <Button 
                variant="outline" 
                className="w-full h-10 border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => handleStartInventory('trial')}
              >
                Durchführen
              </Button>
            </CardContent>
          </Card>

          {/* Abschlussinventur */}
          <Card className={cn(
            "relative overflow-hidden transition-colors",
            !isFinalInventoryAvailable() ? "opacity-75 bg-slate-50" : "hover:border-purple-200"
          )}>
            {!isFinalInventoryAvailable() && (
              <div className="absolute top-2 right-2">
                <AlertCircle className="w-4 h-4 text-slate-400" />
              </div>
            )}
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileDown className="w-5 h-5 text-purple-600" />
                Abschlussinventur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                Jahresabschluss-Inventur. Erzeugt einen PDF-Vergleich und übernimmt die Istmenge.
              </p>
              <Button 
                disabled={!isFinalInventoryAvailable()}
                className="w-full bg-purple-600 hover:bg-purple-700 h-10 shadow-lg shadow-purple-500/20"
                onClick={() => handleStartInventory('final')}
              >
                {isFinalInventoryAvailable() ? 'Jahresinventur Start' : 'Nur zum Jahresende'}
              </Button>
              {!isFinalInventoryAvailable() && (
                <p className="text-[10px] text-amber-600 font-medium text-center italic">
                  * Erst 14 Tage vor Jahresende verfügbar
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* History Section */}
        <Card className="bg-slate-50/50 border-slate-200">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Letzte Berichte</CardTitle>
            <History className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border shadow-sm">
                <div>
                  <p className="font-semibold text-sm">Probeinventur Getränke</p>
                  <p className="text-[10px] text-slate-500">20.03.2026 - Abweichung: -2,5%</p>
                </div>
                <Button variant="ghost" size="sm" className="h-8 text-blue-600">Bericht PDF</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (activeTab === 'counting') {
    return (
      <PageShell
        head={(
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setActiveTab('selection')} className="h-8 w-8">
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </Button>
                <h1 className="text-xl font-bold">
                  {inventoryType === 'opening' ? 'Eröffnung' : inventoryType === 'trial' ? 'Probe' : 'Jahresabschluss'} Zählung
                </h1>
              </div>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 h-9 shadow-lg shadow-emerald-500/20"
                onClick={handleFinishCounting}
              >
                Fertigstellen
              </Button>
            </div>
        )}
      >

        {/* Confirmation Dialog */}
        <Dialog open={isConfirmingFinish} onOpenChange={setIsConfirmingFinish}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Zählung speichern und beenden?
              </DialogTitle>
              <DialogDescription className="py-2">
                Dieser Vorgang gleicht die gezählten Mengen <strong>(Ist)</strong> mit den <strong>Master-Bestandsdaten</strong> ab. Dieser Vorgang kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setIsConfirmingFinish(false)}>
                Abbrechen
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleApplyResults}
                disabled={updateStockMutation.isPending}
              >
                {updateStockMutation.isPending ? 'Wird gespeichert...' : 'Bestätigen und speichern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="border-none shadow-md overflow-hidden">
          <CardHeader className="p-4 border-b bg-slate-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Barcode, SKU oder Produktname..." 
                className="pl-9 h-11 bg-white border-slate-200" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y max-h-[65vh] overflow-y-auto">
              {filteredProducts.map(p => {
                let variants = p.variants || [];
                if (typeof variants === 'string') { try { variants = JSON.parse(variants); } catch { variants = []; } }
                const hasVariants = variants.length > 0;

                return (
                  <div key={p.id} className="bg-white">
                    {/* Product Header Row */}
                    <div className="p-4 flex items-center justify-between gap-4 border-b bg-slate-50/30">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded border bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {p.image_url ? <img loading="lazy" decoding="async" src={p.image_url} alt="" className="w-full h-full object-contain" /> : <Package className="w-5 h-5 text-slate-300" />}
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-900 leading-tight">{p.name}</p>
                          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{p.sku || 'KEINE SKU'}</p>
                        </div>
                      </div>
                      {!hasVariants && (
                        <div className="w-28 flex flex-col items-end gap-1">
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Menge ({p.unit || 'Stk'})</span>
                          <Input 
                            type="number" 
                            placeholder="0"
                            className="text-right h-10 font-black text-lg border-2 focus:border-purple-500"
                            value={counts[p.id]}
                            onChange={(e) => setCounts(prev => ({ ...prev, [p.id]: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>

                    {/* Variant Counting Rows */}
                    {hasVariants && (
                      <div className="pl-8 bg-blue-50/5 divide-y divide-blue-100/50">
                        {variants.map((v, idx) => (
                          <div key={`${p.id}_var_${idx}`} className="p-3 pr-4 flex items-center justify-between gap-4 border-l-2 border-blue-200">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded border bg-white flex items-center justify-center overflow-hidden shrink-0">
                                {v.image || p.image_url ? <img loading="lazy" decoding="async" src={v.image || p.image_url} alt="" className="w-full h-full object-contain" /> : <Package className="w-4 h-4 text-slate-200" />}
                              </div>
                              <span className="text-xs font-bold text-blue-700 uppercase">{v.value}</span>
                            </div>
                            <div className="w-24">
                              <Input 
                                type="number" 
                                placeholder="0"
                                className="text-right h-9 font-bold text-base border-blue-100 focus:border-blue-500 bg-white"
                                value={counts[`${p.id}_var_${idx}`]}
                                onChange={(e) => setCounts(prev => ({ ...prev, [`${p.id}_var_${idx}`]: e.target.value }))}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (activeTab === 'results') {
    return (
      <PageShell
        head={(
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <h1 className="text-xl font-bold">Inventur Ergebnisse</h1>
                {hasSaved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 ml-2">AKTUALISIERT / GESPEICHERT</Badge>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setActiveTab(hasSaved ? 'selection' : 'counting')}>
                  {hasSaved ? 'Hauptmenü' : 'Zurück'}
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 border-blue-200 text-blue-700" onClick={handleExportPDF}>
                  <FileDown className="w-4 h-4" /> PDF Export
                </Button>
              </div>
            </div>
        )}
      >

        <Card className="border-none shadow-md overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-4 font-bold text-slate-500 uppercase text-[10px]">Produkt Details</th>
                    <th className="text-center p-4 font-bold text-slate-500 uppercase text-[10px]">Soll (System)</th>
                    <th className="text-center p-4 font-bold text-slate-500 uppercase text-[10px]">Ist (Gezählt)</th>
                    <th className="text-right p-4 font-bold text-slate-500 uppercase text-[10px]">Differenz</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {products.map(p => {
                    let variants = p.variants || [];
                    if (typeof variants === 'string') { try { variants = JSON.parse(variants); } catch { variants = []; } }
                    const hasVariants = variants.length > 0;

                    // Only show products that have counts or differences if saved, else show all
                    const hasAnyCount = hasVariants 
                      ? variants.some((v, idx) => counts[`${p.id}_var_${idx}`] !== '')
                      : counts[p.id] !== '';

                    if (!hasAnyCount && hasSaved) return null;

                    if (!hasVariants) {
                      const ist = parseInt(counts[p.id]) || 0;
                      const soll = p.stock || 0;
                      const diff = ist - soll;
                      return (
                        <tr key={p.id} className="hover:bg-slate-50/30">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded border bg-white flex items-center justify-center overflow-hidden shrink-0">
                                {p.image_url ? <img loading="lazy" decoding="async" src={p.image_url} alt="" className="w-full h-full object-contain" /> : <Package className="w-4 h-4 text-slate-300" />}
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-900">{p.name}</span>
                                <span className="text-[10px] text-slate-400 font-mono uppercase">{p.sku}</span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-center font-medium text-slate-500">{soll}</td>
                          <td className="p-4 text-center font-black text-slate-900">{ist}</td>
                          <td className={cn(
                            "p-4 text-right font-black",
                            diff === 0 ? "text-slate-300" : diff > 0 ? "text-emerald-600" : "text-red-600"
                          )}>
                            {diff > 0 ? `+${diff}` : diff}
                          </td>
                        </tr>
                      );
                    }

                    // Has Variants
                    return (
                      <React.Fragment key={p.id}>
                        <tr className="bg-slate-50/50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded border bg-white flex items-center justify-center overflow-hidden shrink-0">
                                {p.image_url ? <img loading="lazy" decoding="async" src={p.image_url} alt="" className="w-full h-full object-contain" /> : <Package className="w-4 h-4 text-slate-300" />}
                              </div>
                              <span className="font-black text-slate-900">{p.name} (Gesamt)</span>
                            </div>
                          </td>
                          <td colSpan="3" className="p-4 text-right">
                            <Badge variant="outline" className="text-[10px] uppercase font-bold">{variants.length} Varianten</Badge>
                          </td>
                        </tr>
                        {variants.map((v, idx) => {
                          const istStr = counts[`${p.id}_var_${idx}`];
                          const ist = parseInt(istStr) || 0;
                          const soll = v.stock || 0;
                          const diff = ist - soll;

                          if (istStr === '' && hasSaved) return null;

                          return (
                            <tr key={`${p.id}_var_${idx}`} className="bg-white border-l-4 border-blue-100">
                              <td className="p-3 pl-12">
                                <span className="text-xs font-bold text-blue-700 uppercase">→ {v.value}</span>
                              </td>
                              <td className="p-3 text-center text-xs text-slate-500 font-medium">{soll}</td>
                              <td className="p-3 text-center text-xs font-black text-slate-900">{ist}</td>
                              <td className={cn(
                                "p-3 text-right text-xs font-black",
                                diff === 0 ? "text-slate-300" : diff > 0 ? "text-emerald-600" : "text-red-600"
                              )}>
                                {diff > 0 ? `+${diff}` : diff}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {inventoryType !== 'trial' && !hasSaved && (
          <div className="p-5 bg-amber-50 border-2 border-amber-200 rounded-2xl space-y-4 shadow-lg shadow-amber-500/10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0 border border-amber-200">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-base font-black text-amber-900 uppercase tracking-tight">Bestände Synchronisieren?</p>
                <p className="text-sm text-amber-700 leading-relaxed">
                  Dieser Vorgang gleicht die gezählten Mengen <strong>(Ist)</strong> mit den <strong>Master-Bestandsdaten</strong> im Admin-Bereich ab. Dieser Vorgang kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-lg font-bold shadow-lg shadow-amber-600/20"
              onClick={handleApplyResults}
              disabled={updateStockMutation.isPending}
            >
              {updateStockMutation.isPending ? 'Synchronisierung...' : 'Master Stok Verilerini Güncelle'}
            </Button>
          </div>
        )}

        {hasSaved && (
          <div className="p-6 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto border border-emerald-200">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-black text-emerald-900 uppercase">Erfolgreich Abgeschlossen</h3>
            <p className="text-emerald-700 max-w-md mx-auto">
              Die Inventurdaten wurden erfolgreich im System gespeichert und die Master-Bestandsdaten wurden aktualisiert.
            </p>
            <div className="pt-2">
              <Button onClick={() => setActiveTab('selection')} className="bg-emerald-600 hover:bg-emerald-700">
                Zurück zum Hauptmenü
              </Button>
            </div>
          </div>
        )}
      </PageShell>
    );
  }

  return null;
}
